import { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import React, { createContext, useCallback, useContext } from 'react';

import {
  Address,
  BillingAddress,
  ContactInfo,
  ShippingOption,
} from './CheckoutStateProvider.model';

// const LocalStorageKey = 'checkout_state';

export enum CheckoutStepStatus {
  DRAFT = 'draft',
  DONE = 'done',
}

export interface CheckoutStep<Data = Record<string, any>> {
  status?: CheckoutStepStatus;
  data: Data;
}

interface DetailsStep extends CheckoutStep {
  data: {
    contactInfo?: ContactInfo | undefined | null;
    shippingAddress?: Address | undefined | null;
    taxQuote?: number | undefined | null;
  };
}

interface ShippingStep extends CheckoutStep {
  data: {
    shippingOption?: ShippingOption | undefined | null;
  };
}

interface PaymentStep extends CheckoutStep {
  data: {
    billingAddress?: BillingAddress | undefined | null;
    nameOnCard?: string | undefined | null;
    card?: StripeCardElementChangeEvent; // TODO: double check if this is the correct type
  };
}

interface MedicalStep extends CheckoutStep {
  data: {
    isCurrentlyPregnant?: string;
    hasHadHysterectomy?: string;
    pregnancyWeeks?: number;
    hasHadMenstrualPeriodInPastYear?: string;
    lastMenstrualPeriodDate?: string;
    menstrualCycleDurationDays?: number;
  };
}

export type ECommerceCheckoutState = {
  details?: DetailsStep | undefined;
  shipping?: ShippingStep | undefined;
  payment?: PaymentStep | undefined;
  medical?: MedicalStep | undefined;
};

export type CheckoutStateValue<T> = {
  state: T;
  completeStep: (args: { key: keyof T; data: any }) => void;
  resetStep: (args: { key: keyof T; data: any }) => void;
  setTaxQuote: (taxQuote: number | undefined) => void;
};

const CheckoutState =
  createContext<any extends object ? CheckoutStateValue<any> : undefined>(
    undefined,
  );

type CheckoutStateProviderProps<T> = {
  children: React.ReactNode;
  initialState?: T;
};

export function CheckoutStateProvider<T>({
  children,
  initialState,
}: CheckoutStateProviderProps<T>): JSX.Element {
  const [state, setState] = React.useState<T | undefined>(initialState);

  const completeStep = useCallback(
    ({ key, data }: { key: string; data: any }) => {
      setState((prevState: any) => ({
        ...prevState,
        [key]: {
          ...(prevState[key] as CheckoutStep),
          status: CheckoutStepStatus.DONE,
          data: Object.assign({}, data),
        },
      }));
    },
    [],
  );

  const resetStep = useCallback(({ key, data }: { key: string; data: any }) => {
    setState((prevState: any) => ({
      ...prevState,
      [key]: {
        status: CheckoutStepStatus.DRAFT,
        data: {
          ...prevState[key]?.data,
          ...data,
        },
      },
    }));
  }, []);

  const setTaxQuote = useCallback((taxQuote: number) => {
    setState((prevState: any) => ({
      ...prevState,
      details: {
        ...(prevState.details as DetailsStep),
        data: {
          ...prevState.details?.data,
          taxQuote,
        },
      },
    }));
  }, []);

  //   TODO: Uncomment this code to persist the state in the local storage
  //   Still need to figure out how to handle the state when the user navigates away from the checkout

  //   useEffect(() => {
  //     localStorage.setItem(LocalStorageKey, JSON.stringify(state));
  //   }, [state]);

  //   useEffect(() => {
  //     const storedState = localStorage.getItem(LocalStorageKey);
  //     if (storedState) {
  //       setState(JSON.parse(storedState));
  //     }
  //   }, []);

  return (
    <CheckoutState.Provider
      value={
        { state, completeStep, resetStep, setTaxQuote } as CheckoutStateValue<T>
      }
    >
      {children}
    </CheckoutState.Provider>
  );
}

export function useCheckoutState<T = ECommerceCheckoutState>() {
  const context = useContext<CheckoutStateValue<T> | undefined>(CheckoutState);
  if (context === undefined) {
    throw new Error(
      'useCheckoutState must be used within a CheckoutStateProvider',
    );
  }

  return context;
}

// add types to the function
